/**
 * The migration runner.
 *
 * The situation this is built for: someone runs OKANE offline for months, the
 * app keeps shipping schema changes, and one day they come back online, take
 * the update, and their database is many versions behind. It must then walk
 * every migration between where they are and where the code is — in order,
 * once each, without losing data if something fails partway.
 *
 * How the guarantees are met:
 *   - `user_version` is the single source of truth for how far a database has
 *     got. Each migration bumps it inside the same transaction that does the
 *     work, so a crash can never leave a half-applied version: either the whole
 *     migration committed and the pointer moved, or neither did.
 *   - Because the pointer is crash-consistent, resuming is automatic — the next
 *     run simply asks again which versions are still ahead of it.
 *   - A `_migrations` ledger records what ran and when, for history and for
 *     adopting databases created before the ledger existed.
 *   - A newer database than the running code (a stale cached bundle) is refused
 *     rather than migrated, so old code can never corrupt new data.
 *
 * The migrations are the portable `.sql` files in `schema/migrations/` (format:
 * `migration-files.js`). The Go server runs the same files with the same rules —
 * guard, transaction, ledger row, `user_version` — in `schema/schema.go`, and
 * both are checked against `schema/current.sql`. Change the rules in both.
 */
import { LEDGER_SQL, MIGRATIONS } from './migrations.generated.js'

const LEDGER = '_migrations'

/**
 * The first format: nineteen migrations written in JavaScript, replaced by one
 * baseline. A database whose ledger ends at the last of them is exactly the
 * baseline, and is adopted as it; one that stopped earlier cannot be brought up,
 * because the steps between are gone.
 */
const LEGACY = { first: 'core-tables', last: { version: 19, name: 'plans-that-set-themselves' } }

export class MigrationError extends Error {
  constructor(code, message, detail = {}) {
    super(message)
    this.name = 'MigrationError'
    this.code = code // 'db-ahead' | 'failed' | 'invalid-migrations' | 'legacy-behind'
    Object.assign(this, detail)
  }
}

/** Validate the developer-authored list: unique, contiguous 1..N, has an `up`. */
function normalise(migrations) {
  const sorted = [...migrations].sort((a, b) => a.version - b.version)
  sorted.forEach((m, i) => {
    if (!Number.isInteger(m.version) || m.version < 1)
      throw new MigrationError('invalid-migrations', `Migration ${i} has a bad version: ${m.version}`)
    if (m.version !== i + 1)
      throw new MigrationError(
        'invalid-migrations',
        `Migrations must be contiguous from 1; expected ${i + 1} but found ${m.version} (${m.name}).`,
      )
    if (typeof m.up !== 'string' && typeof m.up !== 'function')
      throw new MigrationError('invalid-migrations', `Migration ${m.version} (${m.name}) has no usable "up".`)
  })
  return sorted
}

async function ensureLedger(driver, ledgerSql = LEDGER_SQL) {
  await driver.exec(ledgerSql)
}

/**
 * A database from the first format becomes a baseline database: its ledger is
 * rewritten to the one baseline row and `user_version` to 1. Its schema is
 * already the baseline's, which `test/migrate.mjs` checks.
 */
async function adoptLegacy(driver, sorted) {
  const [first] = await driver.all(`SELECT name FROM ${LEDGER} WHERE version = 1`)
  if (first?.name !== LEGACY.first) return
  const [last] = await driver.all(`SELECT version, name FROM ${LEDGER} ORDER BY version DESC LIMIT 1`)
  if (Number(last.version) !== LEGACY.last.version || last.name !== LEGACY.last.name) {
    throw new MigrationError(
      'legacy-behind',
      `This database stopped at v${last.version} of OKANE's first schema, and the steps from there ` +
        `have been retired. Open it in a version of OKANE from before September 2026, or restore a newer backup.`,
      { dbVersion: Number(last.version) },
    )
  }
  const baseline = sorted[0]
  await driver.exec('BEGIN')
  try {
    await driver.run(`DELETE FROM ${LEDGER}`)
    await driver.run(`INSERT INTO ${LEDGER} (version, name, applied_at) VALUES (?, ?, ?)`, [
      baseline.version,
      baseline.name,
      new Date().toISOString(),
    ])
    await driver.setPragma('user_version', baseline.version)
    await driver.exec('COMMIT')
  } catch (err) {
    await driver.exec('ROLLBACK')
    throw err
  }
  await driver.flush?.()
}

/**
 * A database created before this ledger existed records its progress only in
 * `user_version`. Backfill the ledger so its history is complete and future
 * logic can rely on it.
 */
async function adoptExisting(driver, sorted) {
  const [{ n } = { n: 0 }] = await driver.all(`SELECT COUNT(*) AS n FROM ${LEDGER}`)
  if (Number(n) > 0) return
  const dbVersion = Number((await driver.pragma('user_version')) || 0)
  if (dbVersion <= 0) return
  const nameOf = new Map(sorted.map((m) => [m.version, m.name]))
  const now = new Date().toISOString()
  for (let v = 1; v <= dbVersion; v++) {
    await driver.run(
      `INSERT OR IGNORE INTO ${LEDGER} (version, name, applied_at) VALUES (?, ?, ?)`,
      [v, nameOf.get(v) ?? `adopted-v${v}`, now],
    )
  }
}

/**
 * Where a database stands relative to the code. Read-only apart from creating
 * the ledger and adopting a pre-ledger database.
 *
 * @returns {{ dbVersion, codeVersion, pending, ahead, applied }}
 */
export async function inspectMigrations(driver, migrations = MIGRATIONS, { ledgerSql } = {}) {
  const sorted = normalise(migrations)
  await ensureLedger(driver, ledgerSql)
  await adoptLegacy(driver, sorted)
  await adoptExisting(driver, sorted)

  const dbVersion = Number((await driver.pragma('user_version')) || 0)
  const codeVersion = sorted.length ? sorted[sorted.length - 1].version : 0
  const applied = (await driver.all(`SELECT version, name, applied_at FROM ${LEDGER} ORDER BY version`)).map(
    (r) => ({ version: Number(r.version), name: r.name, appliedAt: r.applied_at }),
  )
  return {
    dbVersion,
    codeVersion,
    applied,
    pending: sorted.filter((m) => m.version > dbVersion),
    ahead: dbVersion > codeVersion,
  }
}

/** A guard's verdict: the first value of its first row, read as a truth. */
async function guardHolds(driver, sql) {
  const [row] = await driver.all(sql)
  const value = row ? Object.values(row)[0] : null
  return value !== null && value !== undefined && value !== 0 && value !== '0' && value !== false
}

const makeCtx = (driver) => ({
  run: (sql, params) => driver.run(sql, params),
  all: (sql, params) => driver.all(sql, params),
  exec: (sql) => driver.exec(sql),
})

/**
 * Apply every pending migration, in order.
 *
 * @param {object}   o
 * @param {object}   o.driver
 * @param {Array}    [o.migrations]
 * @param {(e:object)=>void} [o.onProgress]  phase 'backup' | 'migrating' | 'done'
 * @param {(bytes:Uint8Array, meta:object)=>Promise<void>} [o.backup]
 *        called once, before the first migration, with a snapshot to fall back
 *        on. Skipped for an empty database (nothing to lose).
 * @returns {{ from, to, applied: number[], durationMs }}
 */
export async function runMigrations({ driver, migrations = MIGRATIONS, onProgress, backup, ledgerSql } = {}) {
  const started = Date.now()
  const state = await inspectMigrations(driver, migrations, { ledgerSql })

  if (state.ahead) {
    throw new MigrationError(
      'db-ahead',
      `This copy of OKANE (schema v${state.codeVersion}) is older than your data ` +
        `(v${state.dbVersion}). Update the app before continuing.`,
      { dbVersion: state.dbVersion, codeVersion: state.codeVersion },
    )
  }
  if (!state.pending.length) {
    return { from: state.dbVersion, to: state.dbVersion, applied: [], durationMs: 0 }
  }

  const from = state.dbVersion
  if (backup && from > 0) {
    onProgress?.({ phase: 'backup', total: state.pending.length })
    const bytes = await driver.export()
    await backup(bytes, { from, to: state.codeVersion, at: new Date().toISOString() })
  }

  const applied = []
  const total = state.pending.length
  for (let i = 0; i < total; i++) {
    const m = state.pending[i]
    onProgress?.({ phase: 'migrating', index: i, total, version: m.version, name: m.name })
    await driver.exec('BEGIN')
    try {
      // The guard: a migration whose change is already there records itself as
      // applied without running.
      const wanted = m.when ? await guardHolds(driver, m.when) : true
      if (!wanted) {
        // nothing to do — only the ledger and the version move
      } else if (typeof m.up === 'function') await m.up(makeCtx(driver))
      else await driver.exec(m.up)
      await driver.run(
        `INSERT INTO ${LEDGER} (version, name, applied_at) VALUES (?, ?, ?)`,
        [m.version, m.name, new Date().toISOString()],
      )
      await driver.setPragma('user_version', m.version)
      await driver.exec('COMMIT')
    } catch (err) {
      await driver.exec('ROLLBACK')
      throw new MigrationError(
        'failed',
        `Migration ${m.version} (${m.name}) failed: ${err.message}`,
        { version: m.version, name: m.name, applied: [...applied], cause: err },
      )
    }
    // Persist each committed step, so a crash mid-chain resumes from here.
    await driver.flush?.()
    applied.push(m.version)
  }

  onProgress?.({ phase: 'done', total, applied: [...applied] })
  return { from, to: state.codeVersion, applied, durationMs: Date.now() - started }
}
